import clsx from 'clsx';
import styles from './sign-in-email-form.module.scss';
import { Button, InputRedesigned, Typography } from '@/ui-kit';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { SignInEmailType } from '@/schemes/sign-in-email-schema';
import { useTranslation } from 'react-i18next';
import { CloseIcon, IconTooltip, SignInWarnIcon } from '@/assets/icons';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { LangSelector } from '../lang-selector/lang-selector';
import { callYaMetrika } from '@/utils/call-ya-metrika';

interface Props {
    signInEmailMethods: UseFormReturn<SignInEmailType>,
    isLoading: boolean,
    error: string | null,
    setError: Dispatch<SetStateAction<string | null>>,
    sendCode: () => void
}

export const SignInEmailForm: React.FC<Props> = ({ signInEmailMethods, isLoading, error, setError, sendCode, }) => {
    const { t } = useTranslation()

    const {
        register: signInWithEmailRegister,
        handleSubmit: signInWithEmailSubmit,
        formState: { isValid, isDirty },
    } = signInEmailMethods;

    const isConitnueBtnDisabled = !isValid || isLoading
    
    const showSignInViaTg = false

    useEffect(() => {
        callYaMetrika('email_screen_loaded')
    }, [])

    useEffect(() => {
        if (!isValid && isDirty) setError(t('auth.invalid_email'))
        else if (error) setError(null)
    }, [isValid, isDirty, error])

    const onSignInViaTg = () => {
        // TODO: handle click
    }

    const onClearClick = () => {
        signInEmailMethods.reset()
    }
    
    return (
        <FormProvider { ...signInEmailMethods }>
            <form className={styles.wrapper} onSubmit={signInWithEmailSubmit(sendCode)}>
                <Typography
                    variant="p"
                    fontWeight="medium"
                    className={styles.titleForm}
                    center>
                    {t('auth.title')}
                </Typography>
                <Typography
                    variant="p"
                    fontWeight="medium"
                    className={styles.subtitleForm}
                    center>
                    {t('auth.subtitle')}
                </Typography>
                <div className={styles.input}>
                    <InputRedesigned
                        placeholder={t('auth.email_placeholder')}
                        required
                        className={
                            clsx(
                                (
                                    error ||
                                    (!isValid && isDirty)
                                ) &&
                                styles.inputError
                            )
                        }
                        {...signInWithEmailRegister('email', { required: true })} />
                    {
                        isDirty &&
                        <button type="button" className={styles.clearBtn} onClick={onClearClick}>
                            <CloseIcon className={styles.clearBtnIcon} />
                        </button>
                    }
                </div>
                {
                    error &&
                    <div className={styles.errors}>
                        <div className={styles.error}>
                            <SignInWarnIcon className={clsx(styles.errorIcon, styles.emailErrorIcon)} />
                            <Typography className={styles.errorLabel}>
                                {error}
                            </Typography>

                        </div>
                    </div>
                }
                <div className={styles.actions}>            
                    {
                        showSignInViaTg &&
                        <Button
                            textColor="black"
                            className={styles.signInViaTgBtn}
                            isLoading={isLoading}
                            isDisabled={!isLoading}
                            onClick={onSignInViaTg}>{t('auth.sign_in_via_tg')}</Button>
                    }
                    <Button
                        type="submit"
                        variant="stretched"
                        typeColor="white"
                        textColor={'white'}
                        className={
                            clsx(
                                styles.submitBtn,
                                isConitnueBtnDisabled && styles.submitBtnDisabled
                            )
                        }
                        isLoading={isLoading}
                        isDisabled={isConitnueBtnDisabled}>{t('auth.submit_btn')}</Button>
                </div>
                <LangSelector className={styles.langSelector} />
            </form>
        </FormProvider>
    );
}
